import React, {
  useContext, useCallback, useMemo,
} from 'react';
import move from 'lodash-move';
import {
  flow, slice, find, take, findLast, partition,
} from 'lodash/fp';
import {
  View, StyleSheet, RefreshControl, Platform,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { showActionsheet } from '../../utils/actionsheet';
import DraggableFlatList from '../DraggableFlatList';
import { SCROLL_EVENT_THROTTLE } from '../DraggableFlatList/constants';
import { updateTodo } from '../../modules/todo/actions';
import { useTheme } from '../../contexts/theme';
import { isLoggedInSelector } from '../../modules/auth/selectors';
import MultiSelectContext from '../../contexts/MultiSelect';
import RefreshingContext from '../../contexts/RefreshingContext';
import MultiSelectActionBar from './MultiSelectActionBar';
import SwipableRow from './SwipableRow';
import ActionsContext, { Provider } from './ActionsContext';
import ListFooter from './ListFooter';
import l from '../../utils/localization';


const ALLDAY_EVENT_HEIGHT = 70;

const styles = StyleSheet.create({
  flexOne: { flex: 1 },
  contentContainerStyle: { flexGrow: 1 },
  headerWrapper: { height: ALLDAY_EVENT_HEIGHT },
});


const keyExtractor = (item) => item && item.id.toString();

const DraggableList = ({
  data, headerComponentOffset, ...rest
}) => {
  const {
          x,
          leftActions,
          rightActions,
          triggerAction,
          swipeSensitivity,
        } = useContext(ActionsContext),
        { isRefreshing, onRefresh } = useContext(RefreshingContext),
        { colors } = useTheme(),
        isLoggedIn = useSelector(isLoggedInSelector),
        {
          isEnabled, selectAll, setEnabled, isSelected, multiSelected,
        } = useContext(MultiSelectContext),
        dispatch = useDispatch(),
        [
          headerItems,
          sortableItems,
        ] = useMemo(
          () => partition((i) => i.isCalendarEvent && (i.allDay || !i.startSameDay))(data),
          [data],
        );


  const onReorder = useCallback(({ fromIndex, toIndex }) => {
    const findFirstTaskPriorityAfter = (arr, index) => {
      const item = flow(
        slice(index + 1, arr.length),
        find((i) => !i.isCalendarEvent),
      )(arr);
      return item ? item.manualSortOrder + 1 : 0;
    };

    const findFirstTaskPriorityBefore = (arr, index) => {
      const item = flow(
        take(index),
        findLast((i) => !i.isCalendarEvent),
      )(arr);
      return item ? item.manualSortOrder - 1 : 0;
    };

    if (fromIndex === toIndex) {
      return;
    }

    let manualSortOrder,
        postponedUntil;

    if (toIndex === 0) {
      const item = sortableItems[0];


      postponedUntil = item.isCalendarEvent
        ? dayjs(item.startDate).add(-1, 'hours').toDate()
        : item.status === 'postponed' ? item.postponedUntil : undefined;

      manualSortOrder = item.isCalendarEvent ? 0 : item.manualSortOrder + 1;
    } else if (toIndex === sortableItems.length - 1) {
      const item = sortableItems[sortableItems.length - 1];
      postponedUntil = item.isCalendarEvent ? item.endDate : item.status === 'postponed' ? item.postponedUntil : undefined;
      manualSortOrder = item.isCalendarEvent ? 0 : item.manualSortOrder - 1;
    } else {
      const previewData = move(sortableItems, fromIndex, toIndex),
            before = previewData[toIndex - 1],
            after = previewData[toIndex + 1];

      postponedUntil = before.isCalendarEvent ? before.endDate : before.status === 'postponed' ? before.postponedUntil : undefined;
      manualSortOrder = before.isCalendarEvent && after.isCalendarEvent ? 0
        : before.isCalendarEvent ? findFirstTaskPriorityAfter(previewData, toIndex)
          : after.isCalendarEvent ? findFirstTaskPriorityBefore(previewData, toIndex)
            : (before.manualSortOrder + after.manualSortOrder) / 2;
    }

    const beforeChange = sortableItems[fromIndex];

    const status = postponedUntil > new Date() ? 'postponed' : beforeChange.status === 'postponed' ? 'todo' : undefined;
    dispatch(updateTodo(beforeChange, { manualSortOrder, postponedUntil, status }));
  }, [dispatch, sortableItems]);

  const onDropHorizontal = useCallback(({ translationX, index }) => {
    const action = translationX > 0
      ? findLast((a) => a.triggerAt < translationX * swipeSensitivity)(leftActions)
      : find((a) => a.triggerAt > translationX * swipeSensitivity)(rightActions);

    const item = sortableItems[index];

    if (action) {
      const itemsToTrigger = isEnabled && isSelected(item.id)
        ? Object.keys(multiSelected)
        : [item.id];
      x.setValue(translationX);
      triggerAction(action, translationX > 0, itemsToTrigger);
    }
  }, [
    isEnabled,
    isSelected,
    leftActions,
    multiSelected,
    rightActions,
    sortableItems,
    swipeSensitivity,
    triggerAction,
    x,
  ]);

  const displayBottomScrollSheet = useCallback(async () => {
    const index = await showActionsheet([l.cancel, l.selectAll], {
      cancelButtonIndex: 0,
      tintColor: colors.primary,
    });

    if (index === undefined) {
      return;
    }
    const linkIndex = Platform.OS === 'ios' ? index - 1 : index;
    if (linkIndex !== -1) {
      setEnabled(true);
      selectAll(sortableItems.map(({ id }) => id));
    }
  }, [colors.primary, selectAll, setEnabled, sortableItems]);

  const onMomentumScrollBegin = useCallback(({
    nativeEvent: { contentOffset, contentSize, layoutMeasurement },
  }) => {
    if (contentSize.height > layoutMeasurement.height) {
      if (contentOffset.y + layoutMeasurement.height > contentSize.height + 50) {
        displayBottomScrollSheet();
      }
    } else if (contentOffset.y > 50) {
      displayBottomScrollSheet();
    }
  }, [displayBottomScrollSheet]);

  const ListHeaderComponent = useCallback(() => (
    <View>
      { headerItems.map((item, index) => (
        <View key={item.id} style={styles.headerWrapper}>
          <SwipableRow
            index={index}
            item={item}
            isSwipeDisabled
          />
        </View>
      )) }
    </View>
  ), [headerItems]);

  const renderItem = useCallback((
    { item, index },
  ) => (
    <SwipableRow
      index={index}
      item={item}
      isSwipeDisabled={item.isCalendarEvent}
    />
  ), []);

  const refreshControl = useMemo(() => Platform.OS !== 'android' ? (
    <RefreshControl
      refreshing={isRefreshing}
      onRefresh={onRefresh}
      enabled={isLoggedIn}
    />
  ) : null, [isLoggedIn, isRefreshing, onRefresh]);

  return useMemo(() => (
    <View style={styles.flexOne}>
      <DraggableFlatList
        data={sortableItems}
        itemHeight={70}
        alwaysBounceVertical={sortableItems.length > 0}
        backgroundColor={colors.background}
        refreshControl={refreshControl}
        scrollEventThrottle={SCROLL_EVENT_THROTTLE}
        headerComponentOffset={(headerComponentOffset || 0) + (ALLDAY_EVENT_HEIGHT * headerItems.length)}
        keyExtractor={keyExtractor}
        removeClippedSubviews={false}
        overScrollMode='always'
        bounces
        enabled
        onMomentumScrollBegin={onMomentumScrollBegin}
        renderItem={renderItem}
        ListFooterComponent={sortableItems.length > 0 ? ListFooter : null}
        ListHeaderComponent={ListHeaderComponent}
        onDropHorizontal={onDropHorizontal}
        onReorder={onReorder}
        contentContainerStyle={styles.contentContainerStyle}
        {...rest}
      />
    </View>
  ), [
    colors.background,
    headerComponentOffset,
    headerItems.length,
    ListHeaderComponent,
    onDropHorizontal,
    onMomentumScrollBegin,
    onReorder,
    refreshControl,
    renderItem,
    rest,
    sortableItems,
  ]);
};

const DraggableListWithActions = ({
  data, leftActions, rightActions, headerComponentOffset, ...rest
}) => (
  <Provider
    leftActions={leftActions}
    rightActions={rightActions}
    data={data}
  >
    <MultiSelectActionBar data={data} headerComponentOffset={headerComponentOffset}>
      <DraggableList {...rest} data={data} headerComponentOffset={headerComponentOffset} />
    </MultiSelectActionBar>
  </Provider>
);

export default DraggableListWithActions;
